import React, { useMemo, useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import CodeIcon from '@atlaskit/icon/glyph/code';
import SuitcaseIcon from '@atlaskit/icon/glyph/suitcase';
import { JiraIcon, JiraServiceManagementIcon } from '@atlaskit/logo';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message';
import JSMLifeSaverIcon from '@atlassian/jira-icons/src/ui/jsm-life-saver';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { JSM_WAC_LINK, IMPORT_URL_SUFFIX, JIRA_WAC_LINK } from '../../common/constants';
import { X2C_SCREENS } from '../../common/constants/metrics';
import { ProjectTypes } from '../../common/constants/project';
import { PageDescription, PageHeading } from '../../common/ui';
import { SwitchBackToOldExperienceLink } from '../../common/ui/old-experience-link';
import { hasOnlyJWMLicense } from '../../common/utils';
import useAvailableProducts from '../../common/utils/available-products';
import { useProjectData } from '../../controllers/import-state';
import messages from './messages';
import { ProductTile } from './product-tile';

export const SelectProductScreen = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [, { setProjectType }] = useProjectData();

	const onJWMSelected = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'x2cBusinessProjectTile',
			}),
		);
		setProjectType(ProjectTypes.BUSINESS);
	}, [createAnalyticsEvent, setProjectType]);

	const onJSWSelected = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'x2cSoftwareProjectTile',
			}),
		);
		setProjectType(ProjectTypes.SOFTWARE);
	}, [createAnalyticsEvent, setProjectType]);

	const onJSMSelected = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'x2cServiceDeskProjectTile',
			}),
		);
		window.open(IMPORT_URL_SUFFIX, '_blank');
	}, [createAnalyticsEvent]);

	const { availableProducts } = useAvailableProducts();

	const handleLinkClick = useCallback((event: MouseEvent<Element> | KeyboardEvent<Element>) => {
		event.stopPropagation();
	}, []);

	const products = useMemo(
		() =>
			[
				{
					key: CORE_PROJECT,
					heading: messages.jwmProductHeading,
					description: messages.jwmProductDescription,
					icon: (
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						<SuitcaseIcon
							label="Jira"
							primaryColor={token('color.text.accent.blue', '#0052CC')}
							size="xlarge"
						/>
					),
					teams: [messages.marketing, messages.design, messages.hr, messages.legal],
					footer: (
						<PoweredByLinkWrapper>
							<FormattedI18nMessage
								message={formatMessage(messages.poweredByJwmSpork, {
									linkStart: '{linkStart}',
									linkEnd: '{linkEnd}',
								})}
								componentsMapping={{
									link: ({ children }) => (
										<>
											<JiraIcon size="xsmall" appearance="brand" />
											<a href={JIRA_WAC_LINK} target="_blank" onClick={handleLinkClick}>
												{children}
											</a>
										</>
									),
								}}
							/>
						</PoweredByLinkWrapper>
					),
					onClick: onJWMSelected,
				},
				{
					key: SOFTWARE_PROJECT,
					heading: messages.jswProductHeadingNew,
					description: messages.jswProductDescription,
					icon: (
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						<CodeIcon
							label="Jira"
							primaryColor={token('color.text.accent.blue', '#0052CC')}
							size="xlarge"
						/>
					),
					teams: [messages.software, messages.agile, messages.devops],
					footer: (
						<PoweredByLinkWrapper>
							<FormattedI18nMessage
								message={formatMessage(messages.poweredByJswSpork, {
									linkStart: '{linkStart}',
									linkEnd: '{linkEnd}',
								})}
								componentsMapping={{
									link: ({ children }) => (
										<>
											<JiraIcon size="xsmall" appearance="brand" />
											<a href={JIRA_WAC_LINK} target="_blank" onClick={handleLinkClick}>
												{children}
											</a>
										</>
									),
								}}
							/>
						</PoweredByLinkWrapper>
					),
					onClick: onJSWSelected,
				},
				{
					key: SERVICE_DESK_PROJECT,
					heading: messages.jsmProductHeading,
					description: messages.jsmProductDescription,
					icon: (
						<JSMLifeSaverIcon
							label="Jira"
							primaryColor={token('color.text.accent.blue', '#0052CC')}
							size="xlarge"
						/>
					),
					teams: [messages.it, messages.hr, messages.marketing, messages.facilities],
					footer: (
						<PoweredByLinkWrapper>
							<FormattedI18nMessage
								message={formatMessage(messages.poweredByJsm, {
									linkStart: '{linkStart}',
									linkEnd: '{linkEnd}',
								})}
								componentsMapping={{
									link: ({ children }) => (
										<>
											<JiraServiceManagementIcon size="xsmall" appearance="brand" />
											<a href={JSM_WAC_LINK} target="_blank" onClick={handleLinkClick}>
												{children}
											</a>
										</>
									),
								}}
							/>
						</PoweredByLinkWrapper>
					),
					onClick: onJSMSelected,
				},
			].filter((product) => availableProducts.has(product.key)),
		[
			formatMessage,
			onJWMSelected,
			onJSWSelected,
			onJSMSelected,
			handleLinkClick,
			availableProducts,
		],
	);

	// TODO: If user is not SITE_ADMIN, redirect to JWM upload screen immediately as they can't use the legacy importer
	if (hasOnlyJWMLicense(availableProducts)) {
		// If instance only has JWM license, skip Project select step
		onJWMSelected();
		return null;
	}

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.SELECT_PRODUCT} sourceType={SCREEN}>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.select-product-screen.wrapper">
				<PageHeading>{formatMessage(messages.heading)}</PageHeading>
				<PageDescription>{formatMessage(messages.description)}</PageDescription>
				<ProductTileWrapper>
					{products.map(({ key, ...props }) => (
						<ProductTile key={key} product={key} {...props} />
					))}
				</ProductTileWrapper>
				<FooterWrapper>
					<SwitchBackToOldExperienceLink
						content={formatMessage(messages.importIntoExistingJiraProject)}
					/>
				</FooterWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	padding: `88px ${token('space.1000', '80px')} ${token('space.300', '24px')} ${token(
		'space.1000',
		'80px',
	)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProductTileWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'stretch',
	flexFlow: 'wrap',
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PoweredByLinkWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	marginTop: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N200),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '11px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		margin: `0 ${token('space.025', '2px')}`,
	},
});
